import { trace, type Span, type Tracer } from "@opentelemetry/api";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { UserInteractionInstrumentation } from "@opentelemetry/instrumentation-user-interaction";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";
import { onCLS, onINP, onLCP, onFCP, onTTFB } from "web-vitals";

type AttrValue = string | number | boolean;
type Attrs = Record<string, AttrValue>;

interface ObservabilityConfig {
  serviceName: string;
  environment: string;
  version?: string;
  apiBaseUrl: string;
}

let provider: WebTracerProvider | undefined;
let tracer: Tracer | undefined;

export function initObservability(config: ObservabilityConfig): void {
  if (provider) return;

  const resource = resourceFromAttributes({
    "service.name": config.serviceName,
    "deployment.environment.name": config.environment,
    ...(config.version && { "service.version": config.version }),
  });

  const exporter = new OTLPTraceExporter({
    url: `${config.apiBaseUrl}/otel/v1/traces`,
  });

  provider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(exporter)],
  });

  // The default context manager follows context through synchronous code
  // only: a fetch started after an await in a click handler isn't a child of
  // the click span. zone.js would follow it, but it replaces window.Promise,
  // patches browser APIs and keeps unhandled rejections from reaching Sentry.
  provider.register();

  tracer = trace.getTracer(config.serviceName);

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [
          new RegExp(escapeRegExp(config.apiBaseUrl)),
        ],
        clearTimingResources: true,
      }),
      new UserInteractionInstrumentation(),
    ],
  });

  reportWebVitals();
}

// In-memory ring of phase events for the cold-start overlay. Lives outside
// OTel so the overlay still has data when observability is disabled (local
// dev) and so callers don't pay the cost of subscribing to a span processor.
const TIMELINE_MAX = 256;
const timeline: TimelineEntry[] = [];

export interface TimelineEntry {
  readonly name: string;
  readonly t: number;
  readonly attrs?: Attrs | undefined;
}

function pushTimeline(name: string, attrs?: Attrs): void {
  timeline.push({ name, t: performance.now(), attrs });
  if (timeline.length > TIMELINE_MAX) {
    timeline.splice(0, timeline.length - TIMELINE_MAX);
  }
}

function applyAttrs(span: Span, attrs?: Attrs): void {
  if (!attrs) return;
  for (const [key, value] of Object.entries(attrs)) {
    span.setAttribute(key, value);
  }
}

export interface TimedSpan {
  end(extraAttrs?: Attrs): void;
  recordError(error: unknown): void;
}

export function startTimedSpan(name: string, attrs?: Attrs): TimedSpan {
  pushTimeline(`${name}.start`, attrs);
  const span = tracer?.startSpan(name);
  if (span) applyAttrs(span, attrs);
  let ended = false;
  return {
    end(extraAttrs?: Attrs) {
      if (ended) return;
      ended = true;
      if (span) applyAttrs(span, extraAttrs);
      pushTimeline(`${name}.end`, extraAttrs);
      span?.end();
    },
    recordError(error: unknown) {
      if (error instanceof Error) {
        span?.recordException(error);
        span?.setAttribute("exception.type", error.name);
        span?.setAttribute("exception.message", error.message);
      } else {
        span?.setAttribute("exception.message", String(error));
      }
    },
  };
}

export function mark(name: string, attrs?: Attrs): void {
  pushTimeline(name, attrs);
  if (!tracer) return;
  const span = tracer.startSpan(name);
  applyAttrs(span, attrs);
  span.end();
}

export async function forceFlush(): Promise<void> {
  if (!provider) return;
  await provider.forceFlush();
}

export function getColdLoadTimeline(): readonly TimelineEntry[] {
  return timeline;
}

function reportWebVitals(): void {
  if (!tracer) return;

  function reportMetric(name: string, value: number, rating: string): void {
    const span = tracer!.startSpan(`web-vital.${name}`);
    span.setAttribute("web_vital.name", name);
    span.setAttribute("web_vital.value", value);
    span.setAttribute("web_vital.rating", rating);
    span.end();
  }

  onCLS((metric) => reportMetric("CLS", metric.value, metric.rating));
  onINP((metric) => reportMetric("INP", metric.value, metric.rating));
  onLCP((metric) => reportMetric("LCP", metric.value, metric.rating));
  onFCP((metric) => reportMetric("FCP", metric.value, metric.rating));
  onTTFB((metric) => reportMetric("TTFB", metric.value, metric.rating));
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
